import { Button, Snackbar, Stack } from "@mui/material";
import MuiAlert from '@mui/material/Alert';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useXPathData } from "../../hooks/useXPathData";
import { xPathDataColumnsRef } from "../../pages/clientData";
import { FieldMapping } from "../../models/configuration/xpathFieldMap/IXpathFieldMap";
import XPathGridItemDisplayDialog from "./XPathGridItemDisplayDialog";
import XPathGridItemEditDialog from "./XPathGridItemEditDialog";
import XPathGridItemAddDialog from "./xPathGridItemAddDialog";
import { useActions } from "../../hooks/useActions";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../state/store";
import { AlertSeverity,IAlertType } from "../../constants/AlertTypes";
import ConfigLoad from "../shared/config/ConfigLoad";
import "../../assets/gridStyles.css";
import { DtPageHeader } from "../ui/header";
import { DtDataGrid } from "../ui/dataGrid";
import { ActionType } from "../../state/actions";
import { hasPluginConfigChanges } from "../../utils/pluginConfigChangeDetector";
import { hasPushbackConfigChanges } from "../../utils/pushbackConfigChangeDetector";
import { hasDocumentBundlingChanges, hasEventBundlingChanges } from "../../utils/bundlingConfigChangeDetector";
import { consumeGlobalSaveHadChanges } from "../../utils/globalSaveStatus";

interface XPathGridProps {
    isGlobalSave: boolean;
    setIsGlobalSave: () => void;
    setMainAlert: (alert:IAlertType) => void;
    setMainLoading : (val:boolean)=> void;
    onGlobalSaveComplete?: (error?: unknown) => void;
}

const XPathGrid: React.FC<XPathGridProps> = ({ isGlobalSave, setIsGlobalSave, setMainAlert, setMainLoading, onGlobalSaveComplete }) => {

    const XPathData = useXPathData();
    const { saveConfigXPathData } = useActions();
    const dispatch = useDispatch();
    const accessToken = useSelector((state: RootState) => state.appSlice.accessToken);
    const configId = useSelector((state: RootState) => state.appSlice.configId);
    const pluginConfiguration = useSelector((state: RootState) => state.configPlugInData.data?.Configuration);
    const persistedPluginConfiguration = useSelector((state: RootState) => state.configPlugInData.persistedData?.Configuration);
    const pushbackData = useSelector((state: RootState) => state.configPushbackDefaults.data);
    const persistedPushbackData = useSelector((state: RootState) => state.configPushbackDefaults.persistedData);
    const documentBundles = useSelector((state: RootState) => state.configDocumentBundles.data);
    const persistedDocumentBundles = useSelector((state: RootState) => state.configDocumentBundles.persistedData);
    const eventBundles = useSelector((state: RootState) => state.configEventBundles.data);
    const persistedEventBundles = useSelector((state: RootState) => state.configEventBundles.persistedData);
    const [selectedConfigId, setSelectedConfigId] = useState<string>("");
    const [gridData, setGridData] = useState<FieldMapping[]>([]);
    const [selectedItem, setSelectedItem] = useState<FieldMapping | null>(null);
    const [openDisplay, setOpenDisplay] = useState(false);
    const [selectedDisplayItem, setSelectedDisplayItem] = useState<string>('');
    const [openEdit, setOpenEdit] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [saving, setSaving] = useState<boolean>(false);
    const globalSaveInProgressRef = useRef(false);
    
    // State Confirmations
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>('info');

    const updateGridData = useCallback((updatedData: FieldMapping[]) => {
        setGridData(updatedData);
        dispatch({
            type: ActionType.GET_CONFIG_XPATH_DATA_SUCCESS,
            payload: { Configuration: { FieldMappings: updatedData } },
            fromServer: false
        });
    }, [dispatch]);

    useEffect(() => {
        if (XPathData?.Configuration?.FieldMappings) {
            setGridData(XPathData?.Configuration?.FieldMappings);
            setSelectedConfigId(configId ?? '');
        }
    }, [XPathData]);

    const handleAdd = () => { setOpenAdd(true); }

    // Delete Handling
    const handleDelete = (row: FieldMapping, rowIndex?: number) => {
        if (row) {
            let updatedDataGridData = gridData.filter((fieldMapping: FieldMapping) => fieldMapping.Name !== row.Name);
            updateGridData(updatedDataGridData);
            openAlert("Deleted mapping. Click Save to persist changes.", "info");
        }
    }

    const handleEdit = (row: FieldMapping, rowIndex?: number) => {
        if (row) {
            setSelectedItem(row);
            setOpenEdit(true);
        }
    };

    const selectItem = (item: FieldMapping) => {
        setOpenDisplay(true);
        setSelectedDisplayItem(item.Xpath);
    }

    const updateItem = async (item: FieldMapping) => {
        if (selectedItem && item) {
            // Update Grid Items 
            const updatedDataGridData = gridData.map((fieldMapping: FieldMapping) => {
                if (fieldMapping.Name === selectedItem?.Name) {
                    return item;
                }
                return fieldMapping;
            });
            updateGridData(updatedDataGridData);
            setSelectedItem(item);
        }
    }

    const addItem = async (item: FieldMapping) => {
        if (item) {
            if (gridData.some((fieldMapping: FieldMapping) => fieldMapping.Name === item.Name)) {
                openAlert("Field Mapping with same name / description already exists", "warning");
                return;
            }

            const updatedDataGridData = [...gridData, item];
            updateGridData(updatedDataGridData);
        }
    }

    const save = useCallback(async (dataToSave: FieldMapping[] = gridData) => {
        const globalSaved = consumeGlobalSaveHadChanges();
        try {
            setSaving(true);
            const didSave = await saveConfigXPathData(selectedConfigId, dataToSave, accessToken) as unknown as boolean;
            const hasPluginChanges = pluginConfiguration && persistedPluginConfiguration
                ? hasPluginConfigChanges(pluginConfiguration, persistedPluginConfiguration)
                : false;
            const hasPushbackChanges = pushbackData && persistedPushbackData
                ? hasPushbackConfigChanges(pushbackData, persistedPushbackData)
                : false;
            const hasDocumentBundlingChange = documentBundles && persistedDocumentBundles
                ? hasDocumentBundlingChanges(documentBundles, persistedDocumentBundles)
                : false;
            const hasEventBundlingChange = eventBundles && persistedEventBundles
                ? hasEventBundlingChanges(eventBundles, persistedEventBundles)
                : false;
            const hasSaved = didSave === true
                || hasPluginChanges
                || hasPushbackChanges
                || hasDocumentBundlingChange
                || hasEventBundlingChange
                || globalSaved;
            setMainAlert({
                message: hasSaved ? "Successfully Saved Changes" : "No new changes to save",
                severity: hasSaved ? "success" : "info"
            });
        } catch (e) {
            const message = e instanceof Error
                ? `An error occurred while saving changes. ${e.message}`
                : "An error occurred while saving changes. Please try again.";
            setMainAlert({ message, severity: "error" });
            console.error("Error saving data", e);
            throw e;
        } finally {
            setSaving(false);
            setMainLoading(false);
        }
    }, [
        accessToken,
        documentBundles,
        eventBundles,
        gridData,
        persistedDocumentBundles,
        persistedEventBundles,
        persistedPluginConfiguration,
        persistedPushbackData,
        pluginConfiguration,
        pushbackData,
        saveConfigXPathData,
        selectedConfigId,
        setMainAlert,
        setMainLoading
    ]);

    const handleSave = useCallback(async () => {
        if (gridData) {
            setMainLoading(true);
            try {
                await save(gridData);
                onGlobalSaveComplete?.();
            } catch (error) {
                onGlobalSaveComplete?.(error);
                throw error;
            } finally {
                setIsGlobalSave();
            }
        }
    }, [gridData, onGlobalSaveComplete, save, setIsGlobalSave, setMainLoading]);

    useEffect(() => {
        if (isGlobalSave && !globalSaveInProgressRef.current) {
            globalSaveInProgressRef.current = true;
            handleSave()
                .catch(() => {
                    // Errors are surfaced via setMainAlert in save().
                })
                .finally(() => {
                    globalSaveInProgressRef.current = false;
                });
        }
    }, [handleSave, isGlobalSave]);

    // Alert] Handling 
    const handleSnackBarClose = () => setAlertOpen(false);
    const openAlert = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
        setAlertSeverity(severity);
        setAlertMessage(message);
        setAlertOpen(true);
    };


    return (
        <Stack spacing={0} >

            <DtPageHeader actions={<Button
                size="small"
                variant="contained"
                onClick={handleAdd}
            >
                Add XPath Config
            </Button>}> XPath Mapping Configurations</DtPageHeader>

            {/* ConfigLoad component for loading state */}
            <ConfigLoad loading={!XPathData?.Configuration?.FieldMappings || saving} />

            {/* Data Grid */}
            {XPathData?.Configuration?.FieldMappings && !saving && (
                <DtDataGrid
                    data={gridData}
                    columns={xPathDataColumnsRef()}
                    tableName="xPathDataGrid"
                    editConfig={{
                        mode: "custom",
                        onEdit: handleEdit
                    }}
                    deleteConfig={{
                        confirm: true,
                        confirmTitle: "Delete XPath Configurations",
                        confirmMessage: "Are you sure you want to delete the selected XPath configuration?",
                        confirmYesLabel: "Delete",
                        confirmNoLabel: "Cancel",
                        onDelete: (row: any) => handleDelete(row)
                    }}
                    paged
                    responsivePageSize
                    responsivePageConfig={{ heightRatio: 0.85 }}
                />
            )}

            <XPathGridItemDisplayDialog
                open={openDisplay}
                closeFxtn={() => setOpenDisplay(false)}
                item={selectedDisplayItem}
                title="Grid Cell Value (Xpath)"
            />
            <XPathGridItemEditDialog
                open={openEdit}
                closeFxtn={() => setOpenEdit(false)}
                item={selectedItem}
                title="Edit Pushback Field Mapping"
                updateFxtn={updateItem}
                existingItems={gridData}
            />
            <XPathGridItemAddDialog
                open={openAdd}
                closeFxtn={() => setOpenAdd(false)}
                title="Add Pushback Field Mapping"
                updateFxtn={addItem}
                existingItems={gridData}
            />
          
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={alertOpen}
                autoHideDuration={2000}
                onClose={handleSnackBarClose}
            >
                <MuiAlert
                    onClose={handleSnackBarClose}
                    severity={alertSeverity}
                    sx={{ width: '100%', fontSize: '1.2rem', padding: '12px 16px' }}>
                    {alertMessage}
                </MuiAlert>
            </Snackbar>
        </Stack>
    );
}

export default XPathGrid;